import React from 'react';
import { Box, Container, Typography, Button, IconButton, Grid } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import { GitHub, LinkedIn, Email, ArrowOutward } from '@mui/icons-material';
import davidPhoto from '../img/david.jpeg';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const HeroSection = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  position: 'relative',
  paddingTop: '96px',
  paddingBottom: '64px',
  background: theme.palette.background.default,
  [theme.breakpoints.down('md')]: {
    minHeight: 'auto',
    paddingTop: '88px',
    paddingBottom: '64px',
  },
}));

const Eyebrow = styled(Box)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '10px',
  fontFamily: '"JetBrains Mono", ui-monospace, monospace',
  fontSize: '0.8125rem',
  fontWeight: 500,
  letterSpacing: '0.02em',
  color: theme.palette.text.secondary,
  marginBottom: '32px',
  animation: `${fadeIn} 0.5s ease-out`,
  '& .dot': {
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: '#22c55e',
    boxShadow: '0 0 0 4px rgba(34, 197, 94, 0.18)',
  },
}));

const Name = styled(Typography)(({ theme }) => ({
  fontSize: 'clamp(2.75rem, 7vw, 4.75rem)',
  fontWeight: 800,
  letterSpacing: '-0.035em',
  lineHeight: 1.02,
  color: theme.palette.text.primary,
  marginBottom: '20px',
  animation: `${fadeIn} 0.5s ease-out 0.05s backwards`,
}));

const Role = styled(Typography)(({ theme }) => ({
  fontSize: 'clamp(1.25rem, 2.4vw, 1.625rem)',
  fontWeight: 500,
  color: theme.palette.text.secondary,
  marginBottom: '32px',
  animation: `${fadeIn} 0.5s ease-out 0.1s backwards`,
  '& strong': {
    color: theme.palette.text.primary,
    fontWeight: 600,
  },
  '& a': {
    color: theme.palette.primary.main,
    fontWeight: 600,
    borderBottom: `1px solid ${theme.palette.primary.main}`,
    paddingBottom: '1px',
    transition: 'opacity 0.2s ease',
    '&:hover': { opacity: 0.7 },
  },
}));

const Lede = styled(Typography)(({ theme }) => ({
  fontSize: '1.125rem',
  lineHeight: 1.7,
  color: theme.palette.text.secondary,
  maxWidth: '640px',
  marginBottom: '40px',
  animation: `${fadeIn} 0.5s ease-out 0.15s backwards`,
  [theme.breakpoints.down('md')]: {
    fontSize: '1rem',
    marginBottom: '32px',
  },
}));

const Actions = styled(Box)({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: '12px',
  marginBottom: '40px',
  animation: `${fadeIn} 0.5s ease-out 0.2s backwards`,
});

const PrimaryButton = styled(Button)(({ theme }) => ({
  padding: '12px 22px',
  fontSize: '0.95rem',
  fontWeight: 600,
  borderRadius: '10px',
  backgroundColor: theme.palette.text.primary,
  color: theme.palette.background.default,
  '&:hover': {
    backgroundColor: theme.palette.text.primary,
    opacity: 0.9,
  },
}));

const GhostButton = styled(Button)(({ theme }) => ({
  padding: '12px 22px',
  fontSize: '0.95rem',
  fontWeight: 600,
  borderRadius: '10px',
  color: theme.palette.text.primary,
  border: `1px solid ${theme.palette.divider}`,
  '&:hover': {
    borderColor: theme.palette.text.primary,
    backgroundColor: 'transparent',
  },
}));

const PortraitWrap = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  animation: `${fadeIn} 0.6s ease-out 0.25s backwards`,
  [theme.breakpoints.up('md')]: {
    justifyContent: 'flex-end',
  },
}));

const PortraitFrame = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  maxWidth: '380px',
  [theme.breakpoints.down('md')]: {
    maxWidth: '300px',
  },
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: '14px',
  background: theme.palette.background.paper,
  overflow: 'hidden',
  transition: 'border-color 0.25s ease, transform 0.25s ease',
  '&:hover': {
    borderColor: theme.palette.primary.main,
    transform: 'translateY(-2px)',
  },
  // diagonal corner decorations — subtle, only top-left and bottom-right
  '&::before, &::after': {
    content: '""',
    position: 'absolute',
    width: 14,
    height: 14,
    borderColor: theme.palette.primary.main,
    borderStyle: 'solid',
    pointerEvents: 'none',
    opacity: 0.6,
  },
  '&::before': {
    top: -5,
    left: -5,
    borderWidth: '1px 0 0 1px',
  },
  '&::after': {
    bottom: -5,
    right: -5,
    borderWidth: '0 1px 1px 0',
  },
}));

const FrameHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '10px 14px',
  borderBottom: `1px solid ${theme.palette.divider}`,
  fontFamily: '"JetBrains Mono", ui-monospace, monospace',
  fontSize: '0.75rem',
  color: theme.palette.text.secondary,
  '& .accent': {
    color: theme.palette.primary.main,
  },
}));

const PortraitImage = styled('img')({
  display: 'block',
  width: '100%',
  aspectRatio: '4 / 5',
  objectFit: 'cover',
});

const FrameFooter = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '12px 14px',
  borderTop: `1px solid ${theme.palette.divider}`,
  fontFamily: '"JetBrains Mono", ui-monospace, monospace',
  fontSize: '0.75rem',
  color: theme.palette.text.secondary,
  '& .pin': {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
  },
  '& .ping': {
    width: 6,
    height: 6,
    borderRadius: '50%',
    backgroundColor: theme.palette.primary.main,
  },
}));

const SocialIcon = styled(IconButton)(({ theme }) => ({
  width: 40,
  height: 40,
  color: theme.palette.text.secondary,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: '10px',
  '&:hover': {
    color: theme.palette.text.primary,
    borderColor: theme.palette.text.primary,
    backgroundColor: 'transparent',
  },
}));

const Hero = () => {
  return (
    <HeroSection id="home">
      <Container maxWidth="lg">
        <Grid container spacing={{ xs: 6, md: 8 }} alignItems="center">
          <Grid item xs={12} md={7}>
            <Eyebrow>
              <span className="dot" />
              <span>Software engineer · Fargo, ND</span>
            </Eyebrow>

            <Name variant="h1">
              David Owuor,
            </Name>

            <Role>
              <strong>Software Engineer.</strong>
            </Role>

            <Lede>
              I build web applications end-to-end. Right now, that means React,
              TypeScript, and Ruby on Rails. I earned an MS in Computer Science
              from NDSU in December 2025, where my research looked at combining
              LLMs with traditional tools to write better tests. Before this I
              spent a couple of years in academic IT, leading platform
              migrations and writing the documentation people actually read.
            </Lede>

            <Actions>
              <PrimaryButton
                href="#projects"
                disableElevation
              >
                See my work
              </PrimaryButton>

              <GhostButton
                href="/resume.pdf"
                target="_blank"
                rel="noopener noreferrer"
                endIcon={<ArrowOutward sx={{ fontSize: '1rem !important' }} />}
              >
                Resume
              </GhostButton>

              <Box sx={{ display: 'flex', gap: 1, ml: { sm: 1 } }}>
                <SocialIcon
                  href="https://github.com/DMasilva"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="GitHub"
                  size="small"
                >
                  <GitHub fontSize="small" />
                </SocialIcon>
                <SocialIcon
                  href="https://www.linkedin.com/in/david-owuor-082750201/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="LinkedIn"
                  size="small"
                >
                  <LinkedIn fontSize="small" />
                </SocialIcon>
                <SocialIcon
                  href="mailto:david.owuor@ndsu.edu"
                  aria-label="Email"
                  size="small"
                >
                  <Email fontSize="small" />
                </SocialIcon>
              </Box>
            </Actions>
          </Grid>

          <Grid item xs={12} md={5}>
            <PortraitWrap>
              <PortraitFrame>
                <FrameHeader>
                  <Box>
                    <span className="accent">~/</span>
                    <span> DMasilva</span>
                  </Box>
                  <Box component="span">{new Date().getFullYear()}</Box>
                </FrameHeader>

                <PortraitImage
                  src={davidPhoto}
                  alt="Portrait of David Owuor"
                  loading="eager"
                />

                <FrameFooter>
                  <Box className="pin">
                    <span className="ping" />
                    <span>Fargo, ND</span>
                  </Box>
                  <Box component="span">MS CS · &apos;25</Box>
                </FrameFooter>
              </PortraitFrame>
            </PortraitWrap>
          </Grid>
        </Grid>
      </Container>
    </HeroSection>
  );
};

export default Hero;
